<script setup lang="ts">
import type { ITableFilterProps } from '@modules/tables/components/VTableFilter/types';
import type { ITableColumnsOptions } from '@modules/tables/types';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VDownloadReportButton from '@modules/download-report/components/VDownloadReportButton/VDownloadReportButton.vue';
import { useDownloadReport } from '@modules/download-report/composables';
import PendingWithdrawalAcceptButton from '@modules/pending-withdrawals/components/PendingWithdrawalAcceptButton/PendingWithdrawalAcceptButton.vue';
import PendingWithdrawalDenyButton from '@modules/pending-withdrawals/components/PendingWithdrawalDenyButton/PendingWithdrawalDenyButton.vue';
import {
  PENDING_WITHDRAWALS_PERMISSIONS,
  TRANSLATION_GROUP_PENDING_WITHDRAWALS,
} from '@modules/pending-withdrawals/constants';
import { useDownloadPendingWithdrawalsReportMutation } from '@modules/pending-withdrawals/mutations';
import { usePendingWithdrawalsListQuery } from '@modules/pending-withdrawals/queries';
import { ADMIN_PENDING_WITHDRAWALS_VIEW_ROUTE_NAME } from '@modules/pending-withdrawals/submodules/view/constants';
import { ESmartFilterColumnType } from '@modules/smart-filters/enums';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(usePendingWithdrawalsListQuery);

const {
  isReportLoading,
  onDownloadReport,
} = useDownloadReport(useDownloadPendingWithdrawalsReportMutation(), tableState);

const tableFilterOptions: ITableFilterProps = {
  pathForFilter: 'api2/admin/affiliate_cashouts/pending_cashouts',
  actionForFilter: 'list',
  byMerchant: true,
  filterName: 'pending_cashout',
};

const columnOptions: ITableColumnsOptions = {
  merchant_name: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  created_at: {
    type: ESmartFilterColumnType.DATE,
  },
  updated_at: {
    type: ESmartFilterColumnType.DATE,
  },
  creator_id: {
    type: ESmartFilterColumnType.COMPARE,
  },
  creator_type: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  id: {
    type: ESmartFilterColumnType.COMPARE,
  },
  affiliate_id: {
    type: ESmartFilterColumnType.COMPARE,
    defaultMobilePreview: true,
  },
  affiliate_username: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  affiliate_email: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  amount: {
    type: ESmartFilterColumnType.COMPARE,
  },
  payment_system_name: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  payment_system_details: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
};
</script>

<template>
  <VCard v-data-qa-auto-id="'PendingWithdrawalsListPage'" type="table">
    <VTable
      :data="data"
      :state="tableState"
      :loading="isLoading"
      :is-fetching="isFetching"
      :title-t-path="$getTPath(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'HEADER')"
      :filter-props="tableFilterOptions"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VIconButton
          icon="pi-eye"
          :to="{
            name: ADMIN_PENDING_WITHDRAWALS_VIEW_ROUTE_NAME,
            params: {
              id: listItem.id,
            },
          }"
        />
        <PendingWithdrawalAcceptButton
          v-if="$hasPermission(PENDING_WITHDRAWALS_PERMISSIONS.EDIT)"
          :id="listItem.id"
          is-icon
        />
        <PendingWithdrawalDenyButton
          v-if="$hasPermission(PENDING_WITHDRAWALS_PERMISSIONS.EDIT)"
          :id="listItem.id"
          is-icon
        />
      </template>
      <template
        v-if="$hasPermission(PENDING_WITHDRAWALS_PERMISSIONS.DOWNLOAD_LIST)"
        #footerButtons
      >
        <VDownloadReportButton
          :label="$getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'DOWNLOAD_REPORT')"
          :loading="isReportLoading"
          @download="onDownloadReport"
        />
      </template>
    </VTable>
  </VCard>
</template>
